import React from 'react';

export interface TableProps
  extends React.TableHTMLAttributes<HTMLTableElement> {
  /**
   * Table content (thead, tbody, etc.)
   */
  children?: React.ReactNode;
  /**
   * Optional inline style overrides
   */
  style?: React.CSSProperties;
  /**
   * Optional striped rows (alternating background color)
   */
  striped?: boolean;
  /**
   * Optional bordered table (adds borders between cells - Note: Target style uses only header bottom border)
   * @deprecated The target style primarily uses a header bottom border, not full cell borders. Consider if this prop is still needed or should be redefined. For the target style, leave this false.
   */
  bordered?: boolean;
  /**
   * Optional hover effect on rows
   */
  hover?: boolean;
  /**
   * Optional small (compact) table styling
   */
  small?: boolean;
  /**
   * Optional condensed table styling (Bootstrap 3 compat)
   */
  condensed?: boolean;
}

/**
 * A simple Table replacement styled to match the HumanBase data sources appearance (like Screenshot 2).
 * Renders a table with specific styling: header bottom border, optional striping and hover.
 */
const Table: React.FC<TableProps> = ({
  children,
  style = {},
  striped,
  bordered, // Note: `bordered` prop effect is changed to align with screenshot 2
  hover,
  small,
  condensed,
  ...props
}) => {
  // Base styles for the table element
  const baseStyle: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse', // Ensures borders are clean
    borderSpacing: 0, // Remove spacing between cells
    fontSize: small ? '0.875rem' : '1rem', // Adjusted font size (e.g., 14px or 16px)
    marginBottom: '1rem', // Standard spacing below the table
    color: '#212529', // Standard text color
    ...style,
  };

  // Define border color for consistency
  const borderColor = '#dee2e6'; // A common bootstrap-like border color

  // Determine cell border style based on 'bordered' prop - Target style generally avoids cell borders
  // Kept for potential backwards compatibility or specific use cases, but target style doesn't use it.
  const cellBorderStyle = bordered ? `1px solid ${borderColor}` : 'none';

  return (
    // Apply base styles to the table element
    <table style={baseStyle} {...props} role="table">
      {React.Children.map(children, section => {
        // Ensure we are processing valid React elements (like thead, tbody)
        if (!React.isValidElement(section)) return section;

        // Process table sections (thead, tbody, tfoot)
        if (
          section.type === 'thead' ||
          section.type === 'tbody' ||
          section.type === 'tfoot'
        ) {
          // Clone the section to inject props into its children (rows)
          const sectionElement = section as React.ReactElement<{
            children?: React.ReactNode;
          }>;
          return React.cloneElement(sectionElement, {
            children: React.Children.map(
              sectionElement.props.children,
              (row, rowIdx) => {
                // Ensure we are processing valid row elements (tr)
                if (!React.isValidElement(row) || row.type !== 'tr') {
                  return row;
                }

                // Define proper types for row props
                interface RowProps {
                  style?: React.CSSProperties;
                  onMouseEnter?: (
                    e: React.MouseEvent<HTMLTableRowElement>,
                  ) => void;
                  onMouseLeave?: (
                    e: React.MouseEvent<HTMLTableRowElement>,
                  ) => void;
                }

                // Cast row to access its props easily
                const rowElement = row as React.ReactElement<RowProps>;
                let rowBackground = ''; // Default row background

                // Apply striped background if enabled and it's an even-indexed row (1st, 3rd, etc.)
                if (striped && section.type === 'tbody' && rowIdx % 2 === 0) {
                  rowBackground = '#f8f9fa'; // Light gray for striped rows
                }

                // Clone the row element to apply styles and hover effects
                return React.cloneElement(rowElement, {
                  style: {
                    // Apply calculated background color
                    background: rowBackground,
                    // Add transition for smooth hover effect if enabled
                    ...(hover
                      ? { transition: 'background 0.15s ease-in-out' }
                      : {}),
                    // Merge with any existing styles on the row
                    ...rowElement.props.style,
                  },
                  // Apply hover effect: change background on mouse enter
                  onMouseEnter: hover
                    ? (e: React.MouseEvent<HTMLTableRowElement>) => {
                        // Set a slightly darker background on hover
                        (e.currentTarget as HTMLElement).style.background =
                          '#f1f3f5';
                        // Call original onMouseEnter if it exists
                        if (rowElement.props.onMouseEnter) {
                          rowElement.props.onMouseEnter(e);
                        }
                      }
                    : rowElement.props.onMouseEnter,
                  // Apply hover effect: restore background on mouse leave
                  onMouseLeave: hover
                    ? (e: React.MouseEvent<HTMLTableRowElement>) => {
                        // Restore original background (striped or default)
                        (e.currentTarget as HTMLElement).style.background = rowBackground;
                        // Call original onMouseLeave if it exists
                        if (rowElement.props.onMouseLeave) {
                          rowElement.props.onMouseLeave(e);
                        }
                      }
                    : rowElement.props.onMouseLeave,
                });
              },
            ),
          });
        }
        // Return other elements (like captions) unmodified
        return section;
      })}
      {/* Embed CSS styles directly for th and td elements */}
      {/* Using attribute selector for specificity */}
      <style>
        {`
        /* Style for table header (th) and data (td) cells */
        table[role="table"] th,
        table[role="table"] td {
          /* Consistent padding for all cells */
          padding: ${condensed || small ? '5px' : '8px'};
          /* Align text to the left */
          text-align: left;
          /* Vertical alignment in the middle */
          vertical-align: middle;
          /* Cell borders - only applied if 'bordered' prop is true */
          /* Target style (Screenshot 2) does not use these borders */
          border: ${cellBorderStyle};
        }

        /* Specific styles for table header (th) cells */
        table[role="table"] th {
          /* Slightly darker gray background for the header */
          background-color: #e9ecef;
          /* Standard font weight for headers */
          font-weight: 600;
          /* Border only at the bottom of the header cells */
          border-bottom: 1px solid ${borderColor};
           /* Ensure top border is not duplicated if bordered=true */
          border-top: ${bordered ? cellBorderStyle : 'none'};
          border-left: ${bordered ? cellBorderStyle : 'none'};
          border-right: ${bordered ? cellBorderStyle : 'none'};
        }

        /* Remove default bottom border from tbody rows (only header has bottom border) */
        table[role="table"] tbody tr {
          border-bottom: none;
        }
      `}
      </style>
    </table>
  );
};

export default Table;
